import {
  FC,
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';
import {
  StyledLabelWrapper,
  StyledLabel,
} from 'components/Kizen/Textarea/styles';
import {
  StyledInputAreaWithPillsWrapper,
  StyledPill,
  StyledTextareaWithPills,
} from './styles';

interface OutlinedTextareaProps {
  error?: boolean;
  className?: string;
  label?: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  underline?: boolean;
  id?: string;
  rows?: number;
  resize?: string;
  pills: { id: string; value: string }[];
  onRemovePill: (ids: string) => void;
}

export const OutlinedTextarea: FC<OutlinedTextareaProps> = ({
  error = false,
  className = '',
  label,
  value,
  placeholder = '',
  onChange,
  onFocus = null,
  onBlur = null,
  onKeyDown,
  disabled = false,
  underline = false,
  id = '',
  rows = 3,
  pills,
  onRemovePill,
  resize = 'none',
}) => {
  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    onBlur?.(e);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (pills.length > 0) {
      textAreaRef.current?.focus();
    }
  }, [pills.length]);

  return (
    <div className={className}>
      <StyledLabelWrapper htmlFor={id}>
        {label && <StyledLabel>{label}</StyledLabel>}
        <StyledInputAreaWithPillsWrapper
          error={error}
          underline={underline}
          disabled={disabled}
        >
          <div
            className={
              pills.length > 0 ? 'flex pt-[10px] pb-[10px] flex-wrap' : ''
            }
          >
            {pills.map((pill) => (
              <StyledPill
                key={pill.id}
                name={pill.value}
                onRemove={() => onRemovePill(pill.id)}
                hideRemoveIcon={false}
              />
            ))}
          </div>
          <StyledTextareaWithPills
            ref={textAreaRef}
            value={value}
            placeholder={placeholder}
            id={id}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            disabled={disabled}
            rows={rows}
            resize={resize}
            visible={pills.length === 0}
          />
        </StyledInputAreaWithPillsWrapper>
      </StyledLabelWrapper>
    </div>
  );
};
